import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import treeService from '../../services/treeService';
import budgetService from '../../services/budgetService';

const defaultStyles = {
  control: (base) => ({ ...base, height: 38, minHeight: 38, width: '100%' }),
  menu: (base) => ({ ...base, zIndex: 9999, maxHeight: 300, overflow: 'hidden', width: '100%', minWidth: 350 }),
  menuList: (base) => ({ ...base, maxHeight: 280, overflowY: 'auto', padding: 0 }),
  option: (base) => ({ ...base, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' })
};

const COST_PER_UNITS = new Set(['CPA', 'CPM', 'CPV']);

const normalizeUnit = (unit) => (unit || '').toUpperCase();

const formatNumber = (value, { currency = false, decimals = 2, allowZero = false } = {}) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return '—';
  if (numericValue === 0 && !allowZero) return '—';
  const options = {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  };
  if (currency) {
    return `$${numericValue.toLocaleString('en-US', options)}`;
  }
  return numericValue.toLocaleString('en-US', options);
};

const formatUnitValue = (unit, value, { allowZero = false } = {}) => {
  const normalized = normalizeUnit(unit);
  const currencyUnits = COST_PER_UNITS;
  const formatted = formatNumber(value, {
    currency: currencyUnits.has(normalized),
    decimals: currencyUnits.has(normalized) ? 2 : 0,
    allowZero,
  });
  if (formatted === '—') return formatted;
  return `${formatted} ${unit || ''}`.trim();
};

const computeSummaryValue = (unit, summary = {}) => {
  const normalized = normalizeUnit(unit);
  const totals = {
    leads: Number(summary.totalLeadsReal || 0),
    onsiteLeads: Number(summary.totalOnsiteLeadsReal || 0),
    websiteRegistrations: Number(summary.totalWebsiteRegistrationsReal || 0),
    whatsapp: Number(summary.totalWhatsappReal || 0),
    clicks: Number(summary.totalClicksReal || 0),
    linkClicks: Number(summary.totalLinkClicksReal || 0),
    impressions: Number(summary.totalImpressionsReal || 0),
    videoViews: Number(summary.totalVideoViewsReal || 0),
    reach: Number(summary.totalReachReal || 0),
  };
  const totalCost = Number(summary.totalCostReal || summary.totalCost || 0);

  switch (normalized) {
    case 'CLICKS':
      return totals.clicks;
    case 'LINK CLICKS':
      return totals.linkClicks;
    case 'IMPRESIONES':
      return totals.impressions;
    case 'WHATSAPP':
      return totals.whatsapp;
    case 'REGISTROS WEB':
      return totals.websiteRegistrations;
    case 'VISTAS DE VIDEO':
      return totals.videoViews;
    case 'REACH':
      return totals.reach;
    case 'CPA':
      return totals.leads > 0 ? totalCost / totals.leads : 0;
    case 'CPM':
      return totals.impressions > 0 ? (totalCost / totals.impressions) * 1000 : 0;
    case 'CPV':
      return totals.videoViews > 0 ? totalCost / totals.videoViews : 0;
    default:
      return totals.leads;
  }
};

const getSubObjectiveRealValue = (unit, totals, totalCost) => {
  const normalized = normalizeUnit(unit);
  switch (normalized) {
    case 'CLICKS':
      return totals.clicks;
    case 'LINK CLICKS':
      return totals.linkClicks || 0;
    case 'IMPRESIONES':
      return totals.impressions;
    case 'WHATSAPP':
      return totals.whatsapp || 0;
    case 'REGISTROS WEB':
      return totals.websiteRegistrations || 0;
    case 'VISTAS DE VIDEO':
      return totals.videoViews;
    case 'REACH':
      return totals.reach || 0;
    case 'CPA':
      return totals.leads > 0 ? totalCost / totals.leads : 0;
    case 'CPM':
      return totals.impressions > 0 ? (totalCost / totals.impressions) * 1000 : 0;
    case 'CPV':
      return totals.videoViews > 0 ? totalCost / totals.videoViews : 0;
    default:
      return totals.leads;
  }
};

const getSupplementalColumnMeta = (unit, totals, totalCost) => {
  const normalized = normalizeUnit(unit);
  switch (normalized) {
    case 'CLICKS':
      return {
        label: 'CPC',
        value: totals.clicks > 0 ? totalCost / totals.clicks : 0,
        currency: true,
        allowZero: false,
      };
    case 'LINK CLICKS':
      return {
        label: 'Link CPC',
        value: (totals.linkClicks || 0) > 0 ? totalCost / totals.linkClicks : 0,
        currency: true,
        allowZero: false,
      };
    case 'WHATSAPP':
      return {
        label: 'CP-Conv',
        value: (totals.whatsapp || 0) > 0 ? totalCost / totals.whatsapp : 0,
        currency: true,
        allowZero: false,
      };
    case 'REGISTROS WEB':
      return {
        label: 'CP-Reg',
        value:
          (totals.websiteRegistrations || 0) > 0
            ? totalCost / totals.websiteRegistrations
            : 0,
        currency: true,
        allowZero: false,
      };
    case 'IMPRESIONES':
      return {
        label: 'CPM',
        value: totals.impressions > 0 ? (totalCost / totals.impressions) * 1000 : 0,
        currency: true,
        allowZero: false,
      };
    case 'VISTAS DE VIDEO':
      return {
        label: 'CPV',
        value: totals.videoViews > 0 ? totalCost / totals.videoViews : 0,
        currency: true,
        allowZero: false,
        decimals: 4,
      };
    case 'CPM':
      return {
        label: 'Impresiones',
        value: totals.impressions,
        currency: false,
        allowZero: true,
        decimals: 0,
      };
    case 'CPV':
      return {
        label: 'Vistas',
        value: totals.videoViews,
        currency: false,
        allowZero: true,
        decimals: 0,
      };
    case 'CPA':
      return {
        label: 'Leads',
        value: totals.leads,
        currency: false,
        allowZero: true,
        decimals: 0,
      };
    default:
      return {
        label: 'CPL',
        value: totals.leads > 0 ? totalCost / totals.leads : 0,
        currency: true,
        allowZero: false,
      };
  }
};

const getCampaignMetricMeta = (unit, campaign) => {
  const normalized = normalizeUnit(unit);
  switch (normalized) {
    case 'CLICKS':
      return { value: campaign.clicks || 0, currency: false, decimals: 0 };
    case 'LINK CLICKS':
      return { value: campaign.linkClicks || 0, currency: false, decimals: 0 };
    case 'WHATSAPP':
      return { value: campaign.whatsapp || 0, currency: false, decimals: 0 };
    case 'REGISTROS WEB':
      return {
        value: campaign.websiteRegistrations || 0,
        currency: false,
        decimals: 0,
      };
    case 'IMPRESIONES':
      return { value: campaign.impressions || 0, currency: false, decimals: 0 };
    case 'VISTAS DE VIDEO':
      return { value: campaign.videoViews || 0, currency: false, decimals: 0 };
    case 'CPM': {
      const impressions = campaign.impressions || 0;
      const value = impressions > 0 ? (campaign.cost || 0) / impressions * 1000 : 0;
      return { value, currency: true, decimals: 2 };
    }
    case 'CPV': {
      const videoViews = campaign.videoViews || 0;
      const value =
        campaign.cpv ||
        (videoViews > 0 ? (campaign.cost || 0) / videoViews : 0);
      return { value, currency: true, decimals: 2 };
    }
    case 'CPA': {
      const leads = campaign.leads || 0;
      const value = campaign.cpl || (leads > 0 ? (campaign.cost || 0) / leads : 0);
      return { value, currency: true, decimals: 2 };
    }
    default:
      return { value: campaign.leads || 0, currency: false, decimals: 0 };
  }
};

function NodeEditor({ node, objective, clientId, accountId, onChange, allCampaigns, isRootNode = false }) {
  const isLeaf = !node.children || node.children.length === 0;
  const [localNode, setLocalNode] = useState(node);
  const [pendingOption, setPendingOption] = useState(null);
  const [showCustomName, setShowCustomName] = useState(false);
  
  // Validar si es un subobjetivo (no es el nodo raíz)
  const isSubObjective = !isRootNode;

  // Tipos predefinidos de subobjetivos
  const subObjectiveTypes = [
    { value: 'LeadAds', label: 'Lead Ads' },
    { value: 'WhatsApp', label: 'WhatsApp' },
    { value: 'WebSiteConversion', label: 'Website Conversion' },
    { value: 'Impressions', label: 'Impressions' },
    { value: 'Clicks', label: 'Clicks' },
    { value: 'Reach', label: 'Reach' },
    { value: 'VideoViews', label: 'Vistas de Video' },
    { value: 'Custom', label: 'Personalizado...' }
  ];

  useEffect(() => setLocalNode(node), [node]);

  const handleTypeChange = (selectedType) => {
    if (selectedType === 'Custom') {
      setShowCustomName(true);
      const v = { ...localNode, subObjectiveType: selectedType, name: '' };
      setLocalNode(v);
      onChange(v);
    } else {
      setShowCustomName(false);
      const typeData = subObjectiveTypes.find(t => t.value === selectedType);
      const v = { ...localNode, subObjectiveType: selectedType, name: typeData?.label || selectedType };
      setLocalNode(v);
      onChange(v);
    }
  };

  const remainingChildPercent = useMemo(() => {
    if (!localNode.children || localNode.children.length === 0) return 0;
    const total = (localNode.children || []).reduce((acc, n) => acc + Number(n.percent || 0), 0);
    return 100 - total;
  }, [localNode]);


  const handleChildChange = (idx, patch) => {
    const next = { ...localNode, children: [...(localNode.children || [])] };
    next.children[idx] = { ...next.children[idx], ...patch };
    setLocalNode(next);
    onChange(next);
  };

  const addChild = () => {
    const next = { 
      ...localNode, 
      children: [...(localNode.children || []), { 
        tempId: crypto.randomUUID(), 
        name: 'Nuevo Subobjetivo', 
        nodeType: 'group', 
        percent: 0, 
        children: [],
        subObjectiveType: '', // Sin tipo seleccionado inicialmente
        startDate: '', // Usuario debe establecer fecha obligatoriamente
        endDate: '' // Usuario debe establecer fecha obligatoriamente
      }] 
    };
    // Un nodo con hijos no puede tener campañas
    if (next.campaigns && next.campaigns.length) next.campaigns = [];
    setLocalNode(next);
    onChange(next);
  };

  const removeChild = (idx) => {
    const next = { ...localNode, children: (localNode.children || []).filter((_, i) => i !== idx) };
    setLocalNode(next);
    onChange(next);
  };

  const availableCampaignOptions = useMemo(() => {
    const usedIds = new Set((localNode.campaigns || []).map(c => String(c.campaignId)));
    return (allCampaigns || [])
      .filter(c => !usedIds.has(String(c.campaignKey || c.id)))
      .map(c => ({ value: c.campaignKey || c.id, label: c.campaignName || c.name, raw: c }));
  }, [allCampaigns, localNode]);

  const addCampaign = (opt) => {
    if (!opt) return;
    const next = { ...localNode, campaigns: [...(localNode.campaigns || []), { campaignId: opt.value, campaignName: opt.label }] };
    setLocalNode(next);
    onChange(next);
  };

  const removeCampaign = (idx) => {
    const next = { ...localNode, campaigns: (localNode.campaigns || []).filter((_, i) => i !== idx) };
    setLocalNode(next);
    onChange(next);
  };

  const getCampaignLabel = (c) => {
    if (c.campaignName) return c.campaignName;
    const found = (allCampaigns || []).find(x => String(x.campaignKey || x.id) === String(c.campaignId));
    return found?.campaignName || found?.name || `Campaña #${c.campaignId}`;
  };

  return (
    <div className="node-editor">
      <div className="d-flex align-items-end gap-2 mb-2">
        <div className="flex-grow-1">
          <div className="small text-700 mb-1">
            {isRootNode ? 'Objetivo Principal' : 'Tipo de Subobjetivo'}
          </div>
          <div className="d-flex gap-2">
            {isRootNode ? (
              // Para el objetivo padre, mostrar solo el nombre como texto no editable
              <div className="form-control bg-light" style={{ maxWidth: 200 }}>
                {objective?.clientName || 'Cliente'} - {objective?.unit || 'Objetivo'}
              </div>
            ) : (
              // Para subobjetivos, mostrar el combo box editable
              <select
                className="form-select"
                style={{ maxWidth: 200 }}
                value={localNode.subObjectiveType || ''}
                onChange={e => handleTypeChange(e.target.value)}
              >
                <option value="">Seleccionar tipo...</option>
                {subObjectiveTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            )}
            {isRootNode ? (
              // Para el objetivo padre, mostrar el nombre como texto no editable
              <div className="form-control bg-light">
                {localNode.name || `${objective?.clientName || 'Cliente'} - ${objective?.unit || 'Objetivo'}`}
              </div>
            ) : (
              // Para subobjetivos, mostrar campos editables
              <>
                {(showCustomName || localNode.subObjectiveType === 'Custom') && (
                  <input
                    className="form-control"
                    value={localNode.name || ''}
                    onChange={e => { const v = { ...localNode, name: e.target.value }; setLocalNode(v); onChange(v); }}
                    placeholder="Nombre personalizado"
                  />
                )}
                {(!showCustomName && localNode.subObjectiveType && localNode.subObjectiveType !== 'Custom') && (
                  <input
                    className="form-control"
                    value={localNode.name || ''}
                    onChange={e => { const v = { ...localNode, name: e.target.value }; setLocalNode(v); onChange(v); }}
                    placeholder="Personalizar nombre (opcional)"
                  />
                )}
              </>
            )}
          </div>
        </div>
        <div style={{ width: 140 }}>
          <div className="small text-700 mb-1">% del objetivo</div>
          <input
            type="text"
            className="form-control"
            value={localNode.percent ? Number(localNode.percent).toFixed(2) : ''}
            onChange={e => { 
              let val = e.target.value.replace(/[^\d.]/g, ''); // Solo números y punto
              if (val === '') {
                const v = { ...localNode, percent: '' };
                setLocalNode(v);
                onChange(v);
                return;
              }
              const p = parseFloat(val) || 0;
              const v = { ...localNode, percent: p }; 
              setLocalNode(v); 
              onChange(v); 
            }}
            onBlur={e => {
              // Al perder foco, formatear a 2 decimales
              if (localNode.percent !== '') {
                const v = { ...localNode, percent: Number(localNode.percent) };
                setLocalNode(v);
                onChange(v);
              }
            }}
            placeholder="0.00"
          />
        </div>
        <div style={{ width: 180 }}>
          <div className="small text-700 mb-1">Monto del objetivo</div>
          <input
            type="text"
            className="form-control"
            value={localNode.percent ? (Number(objective?.nominalValue || 0) * (Number(localNode.percent) / 100)).toFixed(2) : ''}
            onChange={e => {
              let val = e.target.value.replace(/[^\d.]/g, ''); // Solo números y punto
              if (val === '') {
                const v = { ...localNode, percent: '' };
                setLocalNode(v);
                onChange(v);
                return;
              }
              const amount = parseFloat(val) || 0;
              const total = Number(objective?.nominalValue || 0) || 0;
              const percent = total > 0 ? (amount / total) * 100 : 0;
              const v = { ...localNode, percent };
              setLocalNode(v);
              onChange(v);
            }}
            placeholder="0.00"
          />
        </div>
        {Array.isArray(localNode.children) && localNode.children.length > 0 && (
          <div>
            <span className={`badge ${remainingChildPercent === 0 ? 'bg-success' : 'bg-warning'}`}>
              Restante hijos: {remainingChildPercent.toFixed(2)}%
            </span>
          </div>
        )}
      </div>

      {/* Campos de fecha para subobjetivos */}
      <div className="row g-2 mb-3">
        <div className="col-md-6">
          <div className="small text-700 mb-1">
            Fecha de Inicio
            {isSubObjective && <span className="text-danger ms-1">*</span>}
            {isRootNode && <span className="text-muted ms-1">(heredada del objetivo)</span>}
          </div>
          <input
            type="date"
            className={`form-control ${isSubObjective && !localNode.startDate ? 'is-invalid' : ''}`}
            value={localNode.startDate ? (typeof localNode.startDate === 'string' && localNode.startDate.includes('T') ? localNode.startDate.split('T')[0] : localNode.startDate) : ''}
            onChange={e => { 
              const v = { ...localNode, startDate: e.target.value }; 
              setLocalNode(v); 
              onChange(v); 
            }}
            disabled={isRootNode}
            required={isSubObjective}
          />
          {isSubObjective && !localNode.startDate && (
            <div className="invalid-feedback">
              La fecha de inicio es obligatoria para subobjetivos
            </div>
          )}
        </div>
        <div className="col-md-6">
          <div className="small text-700 mb-1">
            Fecha de Fin
            {isSubObjective && <span className="text-danger ms-1">*</span>}
            {isRootNode && <span className="text-muted ms-1">(heredada del objetivo)</span>}
          </div>
          <input
            type="date"
            className={`form-control ${isSubObjective && !localNode.endDate ? 'is-invalid' : ''}`}
            value={localNode.endDate ? (typeof localNode.endDate === 'string' && localNode.endDate.includes('T') ? localNode.endDate.split('T')[0] : localNode.endDate) : ''}
            onChange={e => { 
              const v = { ...localNode, endDate: e.target.value }; 
              setLocalNode(v); 
              onChange(v); 
            }}
            disabled={isRootNode}
            required={isSubObjective}
          />
          {isSubObjective && !localNode.endDate && (
            <div className="invalid-feedback">
              La fecha de fin es obligatoria para subobjetivos
            </div>
          )}
        </div>
      </div>

      <div className="mb-2 text-700"></div>

      {!isLeaf && (
        <div className="mb-3">
          {(localNode.children || []).map((child, idx) => (
            <div key={child.tempId || child.id || idx} className="d-flex align-items-center gap-2 mb-2">
              <input
                className="form-control"
                value={child.name || ''}
                onChange={(e) => handleChildChange(idx, { name: e.target.value })}
                placeholder="Nombre"
              />
              <input
                type="number"
                className="form-control"
                style={{ maxWidth: 120 }}
                value={child.percent ?? ''}
                onChange={(e) => {
                  const val = e.target.value;
                  const percent = val === '' ? '' : parseFloat(val) || 0;
                  handleChildChange(idx, { percent });
                }}
                min={0}
                max={100}
                placeholder="0"
              />
              <button className="btn btn-outline-danger" onClick={() => removeChild(idx)}>Eliminar</button>
            </div>
          ))}
          <button className="btn btn-outline-primary" onClick={addChild}>Agregar subobjetivo</button>
        </div>
      )}

      {isLeaf && (
        <div className="mb-3">
          <div className="d-flex align-items-center gap-2 mb-2">
            <div style={{ minWidth: 350, flex: 1 }}>
              <Select
                classNamePrefix="react-select"
                options={availableCampaignOptions}
                styles={defaultStyles}
                placeholder="Buscar campaña..."
                onChange={(opt) => setPendingOption(opt)}
                value={pendingOption}
                isClearable
              />
            </div>
            <button
              className="btn btn-outline-primary"
              title="Agregar campaña"
              onClick={() => { addCampaign(pendingOption); setPendingOption(null); }}
              disabled={!pendingOption}
            >
              <span className="fw-bold">+</span>
            </button>
          </div>

          {/* Lista simple de campañas */}
          {(localNode.campaigns || []).length > 0 && (
            <div className="mb-2">
              <div className="small text-700 mb-2">Campañas asignadas:</div>
              {(localNode.campaigns || []).map((c, idx) => (
                <div key={c.campaignId || idx} className="d-flex align-items-center gap-2 mb-1 p-2 bg-light rounded">
                  <div style={{ flex: 1 }}>
                    <span className="fw-medium" title={getCampaignLabel(c)}>{getCampaignLabel(c)}</span>
                  </div>
                  <button className="btn btn-outline-danger btn-sm" onClick={() => removeCampaign(idx)} title="Quitar campaña">
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ObjectiveTreeAllocation({ objective, isBranding }) {
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedPath, setSelectedPath] = useState([0]);
  const [expanded, setExpanded] = useState(new Set());
  const [trackingData, setTrackingData] = useState(null);
  const [loadingTracking, setLoadingTracking] = useState(false);

  const clientId = objective?.clientId;
  const accountId = objective?.accountId;

  const startDate = objective?.startDate;
  const endDate = objective?.endDate;

  const loadCampaigns = useCallback(async () => {
    if (!clientId || !startDate || !endDate) return;

    const formatDate = (d) => {
      if (!d) return undefined;
      if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d)) return d.slice(0, 10);
      const dt = new Date(d);
      if (isNaN(dt)) return undefined;
      return dt.toISOString().slice(0, 10);
    };

    const result = await budgetService.getCampaigns({ 
      clientId, 
      accountId,
      medium: objective?.medium,
      startDate: formatDate(startDate),
      endDate: formatDate(endDate),
    });
    const list = result?.campaigns || result?.data?.campaigns || [];
    
    const targetType = (objective?.campaignType || '').toLowerCase();
    const filtered = targetType 
      ? list.filter((c) => (c.campaignType || '').toLowerCase() === targetType)
      : list;
    
    setCampaigns(filtered);
  }, [clientId, accountId, objective?.campaignType, objective?.medium, startDate, endDate]);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  const loadTree = useCallback(async () => {
    if (!objective?.id) return;
    setLoading(true);
    try {
      const data = await treeService.getTree(objective.id);
      const defaultRootName = `${objective?.clientName || 'Cliente'} - ${
        (objective?.unit || '').trim() ||
        objective?.name ||
        objective?.description ||
        'Objetivo'
      } ${Number(objective?.nominalValue || 0).toLocaleString()}`;
      // Si hay datos guardados, usarlos; si no, crear estructura por defecto
      if (Array.isArray(data) && data.length > 0) {
        setTree(data);
      } else {
        const defaultTree = [
          {
            tempId: crypto.randomUUID(),
            name: defaultRootName,
            nodeType: 'group',
            percent: 100,
            children: [],
            subObjectiveType: '',
            // El nodo padre hereda automáticamente las fechas del objetivo
            startDate: objective?.startDate || '',
            endDate: objective?.endDate || '',
          },
        ];
        setTree(defaultTree);
      }
      setSelectedPath([0]);
      setExpanded(new Set(['0']));
    } finally {
      setLoading(false);
    }
  }, [
    objective?.clientName,
    objective?.description,
    objective?.endDate,
    objective?.id,
    objective?.name,
    objective?.nominalValue,
    objective?.startDate,
    objective?.unit,
  ]);

  useEffect(() => {
    loadTree();
  }, [loadTree]);

  const updateNodeAt = (indices, patch) => {
    const clone = JSON.parse(JSON.stringify(tree));
    let cursor = clone;
    for (let i = 0; i < indices.length - 1; i++) cursor = cursor[indices[i]].children;
    const idx = indices[indices.length - 1];
    cursor[idx] = { ...cursor[idx], ...patch };
    setTree(clone);
  };

  const getNodeAt = (indices) => {
    let node = null;
    let cursor = tree;
    for (let i = 0; i < indices.length; i++) {
      node = cursor[indices[i]];
      cursor = node?.children || [];
    }
    return node;
  };

  const save = async () => {
    // 1) Normalizar árbol (no mezclar hijos y campañas)
    const cleanse = (nodes) => nodes.map(n => {
      const node = { ...n };
      if (node.children && node.children.length > 0) {
        node.campaigns = [];
        node.children = cleanse(node.children);
      }
      return node;
    });
    const normalizedTree = cleanse(tree);

    // 1.5) Validación frontend: cada grupo debe sumar 100% y subobjetivos deben tener fechas
    const validateFrontend = (nodes, isRoot = true) => {
      for (const node of nodes) {
        // Validar fechas obligatorias para subobjetivos (no para el nodo raíz)
        if (!isRoot) {
          if (!node.startDate || node.startDate.trim() === '') {
            throw new Error(`El subobjetivo "${node.name}" debe tener fecha de inicio`);
          }
          if (!node.endDate || node.endDate.trim() === '') {
            throw new Error(`El subobjetivo "${node.name}" debe tener fecha de fin`);
          }
          // Validar que la fecha de inicio sea anterior a la fecha de fin
          if (node.startDate && node.endDate && new Date(node.startDate) >= new Date(node.endDate)) {
            throw new Error(`En "${node.name}": la fecha de inicio debe ser anterior a la fecha de fin`);
          }
        }
        
        // Validación de porcentajes - COMENTADA PARA PERMITIR GUARDAR SIN 100%
        // if (Array.isArray(node.children) && node.children.length > 0) {
        //   const childSum = node.children.reduce((acc, c) => acc + Number(c.percent || 0), 0);
        //   if (Math.abs(childSum - 100) > 0.01) {
        //     throw new Error(`El grupo "${node.name}" debe sumar 100% (actual: ${childSum.toFixed(2)}%)`);
        //   }
        //   validateFrontend(node.children, false); // Los hijos no son raíz
        // }
        
        // Solo validar recursivamente los hijos, sin validar suma de 100%
        if (Array.isArray(node.children) && node.children.length > 0) {
          validateFrontend(node.children, false); // Los hijos no son raíz
        }
        if (Array.isArray(node.campaigns) && node.campaigns.length > 0) {
          const campaignSum = node.campaigns.reduce((acc, c) => acc + Number(c.percent || 0), 0);
          // Solo validar 100% si hay campañas con porcentajes > 0
          const hasNonZeroCampaigns = node.campaigns.some(c => Number(c.percent || 0) > 0);
          if (hasNonZeroCampaigns && Math.abs(campaignSum - 100) > 0.01) {
            throw new Error(`Las campañas en "${node.name}" deben sumar 100% (actual: ${campaignSum.toFixed(2)}%)`);
          }
        }
      }
    };

    try {
      validateFrontend(normalizedTree);
    } catch (validationError) {
      toast.error(validationError.message);
      return;
    }

    try {
      setLoading(true);
      // Enviar el árbol completo al backend, no solo allocations
      await treeService.saveTree(objective.id, normalizedTree);
      
      // Notificación de éxito
      toast.success('Árbol guardado correctamente');
      await loadTree();
    } catch (error) {
      // Notificación de error
      const msg = error?.response?.data?.error?.message || error?.message || 'Error al guardar el árbol';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const loadRealTracking = async () => {
    if (!objective?.id || !tree || tree.length === 0) {
      toast.warning('Primero debes guardar el árbol de objetivos');
      return;
    }

    try {
      setLoadingTracking(true);
      const objectivesService = await import('../../services/objectivesService');
      const response = await objectivesService.default.getTreeRealTracking({
        objectiveId: objective.id,
        tree: tree
      });

      if (response.success) {
        setTrackingData(response.data);
        toast.success('Datos de seguimiento cargados correctamente');
      } else {
        toast.error(response.error || 'Error al cargar seguimiento');
      }
    } catch (error) {
      toast.error('Error al cargar datos de seguimiento: ' + (error.message || 'Error desconocido'));
    } finally {
      setLoadingTracking(false);
    }
  };

  const toggleExpand = (pathKey) => {
    const next = new Set(expanded);
    if (next.has(pathKey)) next.delete(pathKey); else next.add(pathKey);
    setExpanded(next);
  };

  // Drag & Drop helpers (reordenar entre hermanos)
  const [draggingKey, setDraggingKey] = useState(null);

  const moveWithinParent = (parentIndices, fromIndex, toIndex) => {
    const clone = JSON.parse(JSON.stringify(tree));
    let cursor = clone;
    for (let i = 0; i < parentIndices.length; i++) cursor = cursor[parentIndices[i]].children;
    const [moved] = cursor.splice(fromIndex, 1);
    cursor.splice(toIndex, 0, moved);
    setTree(clone);
  };

  const renderTree = (nodes, parentPath = []) => {
    return (
      <ul className="list-unstyled ms-0">
        {(nodes || []).map((n, idx) => {
          const path = [...parentPath, idx];
          const pathKey = path.join('.');
          const isSelected = JSON.stringify(path) === JSON.stringify(selectedPath);
          const isLeaf = !n.children || n.children.length === 0;
          const isOpen = expanded.has(pathKey);
          return (
            <li key={n.tempId || n.id || pathKey} className="mb-1"
                draggable
                onDragStart={(e) => { setDraggingKey(pathKey); e.dataTransfer.setData('text/plain', pathKey); e.dataTransfer.effectAllowed = 'move'; }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const source = draggingKey || e.dataTransfer.getData('text/plain');
                  if (!source) return;
                  const from = source.split('.').map(Number);
                  const to = path;
                  // Solo reordenar entre hermanos
                  if (from.slice(0, -1).join('.') === to.slice(0, -1).join('.')) {
                    const parent = from.slice(0, -1);
                    const fromIdx = from[from.length - 1];
                    const toIdx = to[to.length - 1];
                    if (fromIdx !== toIdx) moveWithinParent(parent, fromIdx, toIdx);
                  }
                  setDraggingKey(null);
                }}
                onDragEnd={() => setDraggingKey(null)}
            >
              <div
                className={`d-flex align-items-center gap-2 px-2 py-1 rounded ${isSelected ? 'bg-primary text-white' : 'bg-light'}`}
                style={{ cursor: 'pointer' }}
                onClick={() => setSelectedPath(path)}
              >
                <span
                  className={`fw-bold ${isLeaf ? 'text-primary' : 'text-muted'}`}
                  onClick={(e) => { e.stopPropagation(); if (!isLeaf) toggleExpand(pathKey); }}
                  style={{ cursor: isLeaf ? 'default' : 'pointer', minWidth: '16px', display: 'inline-block' }}
                >
                  {isLeaf ? '●' : (isOpen ? '▼' : '▶')}
                </span>
                <span className="fw-semi-bold text-truncate" title={n.name}>
                  {parentPath.length === 0
                    ? `${objective?.clientName || 'Cliente'} - ${(objective?.unit || '').trim() || (objective?.name || 'Objetivo')} ${Number(objective?.nominalValue || 0).toLocaleString()}`
                    : n.name}
                </span>
                <span className={`badge ${isSelected ? 'bg-light text-dark' : 'bg-secondary'}`}>{Number(n.percent || 0).toFixed(2)}%</span>
                {/* Solo mostrar botón de eliminar si NO es el objetivo principal (parent.length > 0) */}
                {parentPath.length > 0 && (
                  <button
                    className={`btn btn-sm ${isSelected ? 'btn-light' : 'btn-outline-danger'} ms-auto`}
                    title="Eliminar subobjetivo"
                    onClick={(e) => {
                      e.stopPropagation();
                      const parent = path.slice(0, -1);
                      const index = path[path.length - 1];
                      const clone = JSON.parse(JSON.stringify(tree));
                      if (parent.length === 0) {
                        clone.splice(index, 1);
                      } else {
                        let cursor = clone;
                        for (let i = 0; i < parent.length - 1; i++) cursor = cursor[parent[i]].children;
                        const parentNode = cursor[parent[parent.length - 1]];
                        parentNode.children.splice(index, 1);
                      }
                      setTree(clone);
                      setSelectedPath(parent.length ? parent : [0]);
                    }}
                  >
                    <span className="fw-bold">×</span>
                  </button>
                )}
              </div>
              {!isLeaf && isOpen && (
                <div className="ms-3 mt-1">
                  {renderTree(n.children, path)}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  const breadcrumb = useMemo(() => {
    const names = [];
    let cursor = tree;
    selectedPath.forEach((i) => {
      const node = cursor?.[i];
      if (node) names.push(node.name || 'Nodo');
      cursor = node?.children || [];
    });
    return names;
  }, [selectedPath, tree]);

  const selectedNode = getNodeAt(selectedPath) || null;
  const unitLabel = objective?.unit || 'Leads';
  const normalizedUnit = normalizeUnit(unitLabel);
  const allowZeroForUnit = COST_PER_UNITS.has(normalizedUnit);
  const summaryRealValue = trackingData
    ? computeSummaryValue(unitLabel, trackingData)
    : 0;

  if (!objective) return <div className="alert alert-info">Selecciona un objetivo</div>;

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="d-flex align-items-center gap-3">
          <div>
            <h6 className="mb-0 text-700">{`${objective?.clientName || 'Cliente'} - ${(objective?.unit || '').trim() || (objective?.name || 'Objetivo')} ${Number(objective?.nominalValue || 0).toLocaleString()}`}</h6>
            <small className="text-muted">Objetivo Principal</small>
            {trackingData && (
              <div className="mt-2">
                <span className="badge bg-success me-2">
                  Real: {formatUnitValue(unitLabel, summaryRealValue, { allowZero: allowZeroForUnit })}
                </span>
                <span className="badge bg-info">
                  Objetivo: {formatUnitValue(unitLabel, Number(objective?.nominalValue || 0), { allowZero: true })}
                </span>
              </div>
            )}
          </div>
          <span className="badge bg-secondary">Total: {Number(objective?.nominalValue || 0).toLocaleString()}</span>
        </div>
        <div className="d-flex align-items-center gap-2">
          <button className="btn btn-outline-secondary" onClick={loadTree} title="Recargar">
            <span className="fw-bold">↻</span>
          </button>
          <button 
            className="btn btn-outline-info" 
            onClick={loadRealTracking} 
            disabled={loadingTracking || !tree || tree.length === 0}
            title="Ver datos reales de las campañas"
          >
            {loadingTracking ? (
              <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
            ) : (
              <span className="me-1">📊</span>
            )}
            Seguimiento
          </button>
          <button className="btn btn-primary" onClick={save} disabled={loading}>
            Guardar 
          </button>
        </div>
      </div>

      {loading ? (
        <div className="d-flex align-items-center gap-2 text-muted">
          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          <span>Procesando...</span>
        </div>
      ) : (
        <div className="row g-3">
          <div className="col-12 col-md-4">
            <div className="border rounded p-2" style={{ maxHeight: 520, overflowY: 'auto' }}>
              <div className="d-flex align-items-center justify-content-between mb-2">
                <span className="fw-bold">Árbol de subobjetivos</span>
                <div className="d-flex gap-2">
                  <button className="btn btn-sm btn-outline-primary" onClick={() => {
                    const node = getNodeAt(selectedPath);
                    if (!node) return;
                    const child = { 
                      tempId: crypto.randomUUID(), 
                      name: 'Nuevo Subobjetivo', 
                      nodeType: 'group', 
                      percent: 0, 
                      children: [],
                      subObjectiveType: '',
                      startDate: '', // Usuario debe establecer fecha obligatoriamente
                      endDate: '' // Usuario debe establecer fecha obligatoriamente
                    };
                    const clone = JSON.parse(JSON.stringify(tree));
                    let cursor = clone;
                    for (let i = 0; i < selectedPath.length; i++) cursor = cursor[selectedPath[i]].children;
                    cursor.push(child);
                    setTree(clone);
                    const newPath = [...selectedPath, cursor.length - 1];
                    setSelectedPath(newPath);
                    setExpanded(new Set(Array.from(expanded).concat([selectedPath.join('.')])));
                  }}>+</button>
                </div>
              </div>
              {renderTree(tree)}
            </div>
          </div>
          <div className="col-12 col-md-8">
            <div className="border rounded p-3">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div>
                  <div className="small text-700">Editando</div>
                  <div className="fw-bold">{breadcrumb.join(' › ') || 'Nodo'}</div>
                </div>
                <span className="badge bg-info">{selectedNode ? (selectedNode.children?.length ? 'Grupo' : 'Hoja') : ''}</span>
              </div>
              {selectedNode ? (
                <NodeEditor
                  node={selectedNode}
                  objective={objective}
                  clientId={clientId}
                  accountId={accountId}
                  allCampaigns={campaigns}
                  onChange={(next) => updateNodeAt(selectedPath, next)}
                  isRootNode={selectedPath.length === 1 && selectedPath[0] === 0}
                />
              ) : (
                <div className="alert alert-warning">Selecciona un nodo del árbol para editarlo</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Sección de datos de seguimiento */}
      {trackingData && trackingData.nodesTracking && trackingData.nodesTracking.length > 0 && (
        <div className="mt-4">
          <div className="card">
            <div className="card-header bg-light">
              <h6 className="mb-0">📊 Seguimiento Real por Subobjetivo</h6>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                {(() => {
                  const supplementalLabel = getSupplementalColumnMeta(
                    unitLabel,
                    { leads: 0, clicks: 0, impressions: 0, videoViews: 0 },
                    0,
                  ).label;
                  const nodesCostAggregate =
                    trackingData.nodesTracking?.reduce(
                      (sum, node) =>
                        sum +
                        (node.campaignDetails?.reduce(
                          (s, c) => s + (c.cost || 0),
                          0,
                        ) || 0),
                      0,
                    ) || 0;
                  const totalCostGlobal = Number(
                    (trackingData.totalCostReal ??
                      nodesCostAggregate ??
                      0),
                  );
                  const summaryTotals = {
                    leads: Number(trackingData.totalLeadsReal || 0),
                    onsiteLeads: Number(trackingData.totalOnsiteLeadsReal || 0),
                    websiteRegistrations: Number(trackingData.totalWebsiteRegistrationsReal || 0),
                    whatsapp: Number(trackingData.totalWhatsappReal || 0),
                    clicks: Number(trackingData.totalClicksReal || 0),
                    linkClicks: Number(trackingData.totalLinkClicksReal || 0),
                    impressions: Number(trackingData.totalImpressionsReal || 0),
                    videoViews: Number(trackingData.totalVideoViewsReal || 0),
                    reach: Number(trackingData.totalReachReal || 0),
                  };
                  const summarySupplemental = getSupplementalColumnMeta(
                    unitLabel,
                    summaryTotals,
                    Number(totalCostGlobal),
                  );

                  return (
                <table className="table table-sm table-hover">
                  <thead>
                    <tr>
                      <th>Subobjetivo</th>
                      <th>Período</th>
                      <th className="text-end">Real</th>
                      <th className="text-end">Inversión</th>
                      <th className="text-end">{supplementalLabel}</th>
                      <th>Campañas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trackingData.nodesTracking.map((node, idx) => (
                      <tr key={idx}>
                        <td className="fw-semibold">{node.nodeName}</td>
                        <td>
                          <small className="text-muted">
                            {node.startDate && node.endDate 
                              ? `${node.startDate} → ${node.endDate}` 
                              : 'Sin fechas'}
                          </small>
                        </td>
                        <td className="text-end">
                          {(() => {
                            const totals = {
                              leads: Number(node.totalLeads || 0),
                              onsiteLeads: Number(node.totalOnsiteLeads || 0),
                              websiteRegistrations: Number(node.totalWebsiteRegistrations || 0),
                              whatsapp: Number(node.totalWhatsapp || 0),
                              clicks: Number(node.totalClicks || 0),
                              linkClicks: Number(node.totalLinkClicks || 0),
                              impressions: Number(node.totalImpressions || 0),
                              videoViews: Number(node.totalVideoViews || 0),
                              reach: Number(node.totalReach || 0),
                            };
                            const totalCost =
                              node.campaignDetails?.reduce(
                                (sum, c) => sum + (c.cost || 0),
                                0,
                              ) || 0;
                            const realValue = getSubObjectiveRealValue(
                              unitLabel,
                              totals,
                              totalCost,
                            );

                            return (
                              <span className="badge bg-success">
                                {formatUnitValue(unitLabel, realValue, {
                                  allowZero: allowZeroForUnit,
                                })}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="text-end">
                          ${node.campaignDetails?.reduce((sum, c) => sum + (c.cost || 0), 0).toFixed(2) || 0}
                        </td>
                        <td className="text-end">
                          {(() => {
                            const totals = {
                              leads: Number(node.totalLeads || 0),
                              onsiteLeads: Number(node.totalOnsiteLeads || 0),
                              websiteRegistrations: Number(node.totalWebsiteRegistrations || 0),
                              whatsapp: Number(node.totalWhatsapp || 0),
                              clicks: Number(node.totalClicks || 0),
                              linkClicks: Number(node.totalLinkClicks || 0),
                              impressions: Number(node.totalImpressions || 0),
                              videoViews: Number(node.totalVideoViews || 0),
                              reach: Number(node.totalReach || 0),
                            };
                            const totalCost =
                              node.campaignDetails?.reduce(
                                (sum, c) => sum + (c.cost || 0),
                                0,
                              ) || 0;
                            const supplementalMeta = getSupplementalColumnMeta(
                              unitLabel,
                              totals,
                              totalCost,
                            );

                            return formatNumber(supplementalMeta.value, {
                              currency: supplementalMeta.currency,
                              decimals:
                                supplementalMeta.decimals ??
                                (supplementalMeta.currency ? 2 : 0),
                              allowZero: supplementalMeta.allowZero,
                            });
                          })()}
                        </td>
                        <td>
                          <div className="d-flex flex-column gap-1">
                            {node.campaignDetails?.map((campaign, cidx) => (
                              <small key={cidx} className="text-muted">
                                {campaign.campaignName}:{' '}
                                {(() => {
                                  const metric = getCampaignMetricMeta(
                                    unitLabel,
                                    campaign,
                                  );
                                  return formatNumber(metric.value, {
                                    currency: metric.currency,
                                    decimals:
                                      metric.decimals ??
                                      (metric.currency ? 2 : 0),
                                    allowZero: !metric.currency,
                                  });
                                })()}
                              </small>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="table-info fw-bold">
                      <td colSpan="2">TOTAL GENERAL</td>
                      <td className="text-end">
                        <span className="badge bg-primary">
                          {formatUnitValue(unitLabel, summaryRealValue, {
                            allowZero: allowZeroForUnit,
                          })}
                        </span>
                      </td>
                      <td className="text-end">
                        ${Number(totalCostGlobal).toFixed(2)}
                      </td>
                      <td className="text-end">
                        {formatNumber(summarySupplemental.value, {
                          currency: summarySupplemental.currency,
                          decimals:
                            summarySupplemental.decimals ??
                            (summarySupplemental.currency ? 2 : 0),
                          allowZero: summarySupplemental.allowZero,
                        })}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


